import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { setGlobalNavigator } from '@/utils/navigation'
import { initNativeSocialLogin } from '@/services/authDeepLinkService'
import { ToastProvider } from '@/components/providers/ToastProvider'
import { NotificationProvider } from '@/components/providers/NotificationProvider'
import { useVersionCheck } from '@/hooks/useVersionCheck'
import ScrollToTop from '@/components/common/ScrollToTop'
import { PageErrorBoundary } from '@/components/common/PageErrorBoundary'
import { PageSkeleton } from '@/components/common/PageSkeleton'
import { PremiumUpsellModal } from '@/components/ui/PremiumUpsellModal'
import { CookieConsent } from '@/components/ui/CookieConsent'
import { NetworkStatusHandler } from '@/components/common/NetworkStatusHandler'

/**
 * 🍎 Native platformlarda SocialLogin plugin'ini başlangıçta bir kez initialize eder.
 * Her buton tıklamasında yeniden initialize etmek "already initialized" hatasına neden olabilir.
 */
function NativeSocialLoginInit() {
  useEffect(() => {
    // Yalnız iOS'ta başlatılır; platform kapısı ve hata kaydı servisin içinde.
    void initNativeSocialLogin()
  }, [])

  return null
}

/**
 * 🔗 Referral Capture — ?ref= parametresini URL'den yakalar ve localStorage'a kaydeder.
 * Kullanıcı daha sonra profil sayfasındaki ReferralCodeEntry bileşeninde bu kodu görecektir.
 */
function ReferralCapture() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const refCode = params.get('ref')?.trim().toUpperCase()
      if (refCode && refCode.length >= 5 && refCode.length <= 8) {
        localStorage.setItem('pending_referral_code', refCode)
        // URL'den parametreyi temizle (clean URL)
        params.delete('ref')
        const cleanUrl = params.toString()
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname
        window.history.replaceState({}, '', cleanUrl)
      }
    } catch {
      // Sessiz — localStorage veya URL parse hatası kritik değil
    }
  }, [])
  return null
}

// Eager — PWA start_url and auth (Kritik yükleme yolu)
import Home from '@/pages/Home'
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'

// Lazy — Sayfa geçişlerinde asenkron yüklenir
const Profile = lazy(() => import('@/pages/Profile'))
const Groups = lazy(() => import('@/pages/Groups'))
const GroupDetail = lazy(() => import('@/pages/GroupDetail'))
const DrawCeremony = lazy(() => import('@/pages/DrawCeremony'))
const PaymentPage = lazy(() => import('@/pages/PaymentPage'))
const CreateGroup = lazy(() => import('@/pages/CreateGroup'))
const Invite = lazy(() => import('@/pages/Invite'))
const InviteAccept = lazy(() => import('@/pages/InviteAccept'))
const Onboarding = lazy(() => import('@/pages/Onboarding'))
const Notifications = lazy(() => import('@/pages/Notifications'))
const ResetPassword = lazy(() => import('@/pages/ResetPassword'))
const ScoreHistoryPage = lazy(() => import('@/pages/ScoreHistoryPage'))
const PremiumView = lazy(() => import('@/pages/premium/PremiumView'))

// Lazy — Admin Pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'))
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'))
const AdminGroups = lazy(() => import('@/pages/admin/AdminGroups'))
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'))
const AuditLogs = lazy(() => import('@/pages/admin/AuditLogs'))
const TemplateManager = lazy(() => import('@/pages/admin/TemplateManager'))
const SupportRequests = lazy(() => import('@/pages/admin/SupportRequests'))
const AdminRevenue = lazy(() => import('@/pages/admin/AdminRevenue'))
const AdminOperations = lazy(() => import('@/pages/admin/AdminOperations').then(m => ({ default: m.AdminOperations })))
const AdminBadges = lazy(() => import('@/pages/admin/AdminBadges'))

// Lazy — Legal Pages (Named Exports)
const KVKKPage = lazy(() => import('@/pages/legal/KVKKPage').then(mod => ({ default: mod.KVKKPage })))
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage').then(mod => ({ default: mod.PrivacyPage })))
const TermsPage = lazy(() => import('@/pages/legal/TermsPage').then(mod => ({ default: mod.TermsPage })))
const SupportPage = lazy(() => import('@/pages/legal/SupportPage'))
const PublicSupportPage = lazy(() => import('@/pages/legal/PublicSupportPage'))
/** 🔄 Sessiz güncelleme sonrası versiyon değişimini yönetir */
function VersionChecker() {
  useVersionCheck()
  return null
}

const LazyRoute = ({ element: Component }: { element: React.ElementType }) => (
  <PageErrorBoundary>
    <Suspense fallback={<PageSkeleton />}>
      <Component />
    </Suspense>
  </PageErrorBoundary>
)

function NavigationBridge() {
  const navigate = useNavigate()
  useEffect(() => {
    setGlobalNavigator(navigate)
  }, [navigate])
  return null
}

function App() {
  return (
    <ToastProvider>
      <VersionChecker />
      <NativeSocialLoginInit />
      <BrowserRouter>
        <NavigationBridge />
        <ScrollToTop />
        <ReferralCapture />
        <NotificationProvider>
          <div className="min-h-screen bg-background text-foreground font-sans antialiased selection:bg-primary/20 selection:text-primary">
            <NetworkStatusHandler />
            <Routes>
              {/* Eager Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Lazy App Routes */}
              <Route path="/profile" element={<LazyRoute element={Profile} />} />
              <Route path="/profile/score-history" element={<LazyRoute element={ScoreHistoryPage} />} />
              <Route path="/groups" element={<LazyRoute element={Groups} />} />
              <Route path="/groups/active" element={<LazyRoute element={GroupDetail} />} />
              <Route path="/groups/:groupId/draw" element={<LazyRoute element={DrawCeremony} />} />
              <Route path="/groups/:groupId/payment" element={<LazyRoute element={PaymentPage} />} />
              <Route path="/groups/create-group" element={<LazyRoute element={CreateGroup} />} />
              <Route path="/groups/new" element={<LazyRoute element={CreateGroup} />} />
              <Route path="/invite" element={<LazyRoute element={Invite} />} />
              <Route path="/invite/:code" element={<LazyRoute element={InviteAccept} />} />
              <Route path="/onboarding" element={<LazyRoute element={Onboarding} />} />
              <Route path="/notifications" element={<LazyRoute element={Notifications} />} />
              <Route path="/reset-password" element={<LazyRoute element={ResetPassword} />} />
              <Route path="/premium" element={<LazyRoute element={PremiumView} />} />

              {/* Lazy Admin Routes */}
              <Route path="/admin" element={<LazyRoute element={AdminDashboard} />} />
              <Route path="/admin/users" element={<LazyRoute element={AdminUsers} />} />
              <Route path="/admin/groups" element={<LazyRoute element={AdminGroups} />} />
              <Route path="/admin/settings" element={<LazyRoute element={AdminSettings} />} />
              <Route path="/admin/audit-logs" element={<LazyRoute element={AuditLogs} />} />
              <Route path="/admin/templates" element={<LazyRoute element={TemplateManager} />} />
              <Route path="/admin/support" element={<LazyRoute element={SupportRequests} />} />
              <Route path="/admin/revenue" element={<LazyRoute element={AdminRevenue} />} />
              <Route path="/admin/operations" element={<LazyRoute element={AdminOperations} />} />
              <Route path="/admin/badges" element={<LazyRoute element={AdminBadges} />} />

              {/* Lazy Legal Routes */}
              <Route path="/kvkk" element={<LazyRoute element={KVKKPage} />} />
              <Route path="/gizlilik" element={<LazyRoute element={PrivacyPage} />} />
              <Route path="/hizmet-kosullari" element={<LazyRoute element={TermsPage} />} />
              <Route path="/destek" element={<LazyRoute element={SupportPage} />} />
              <Route path="/iletisim" element={<LazyRoute element={PublicSupportPage} />} />

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <PremiumUpsellModal />
            <CookieConsent />
          </div>
        </NotificationProvider>
      </BrowserRouter>
    </ToastProvider>
  )
}

export default App
